/**
 * Summary Metrics Calculation Utilities
 *
 * Functions for calculating key building energy metrics from simulation results.
 */

import type { ESOData, OutputVariable } from '@/simulation/parsers/types';
import { getTimeSeriesStats } from './aggregation';

/**
 * Summary metric with trend information
 */
export interface SummaryMetric {
  /** Metric title */
  title: string;
  /** Numeric value */
  value: number;
  /** Unit of measure */
  unit: string;
  /** Formatted display value */
  displayValue: string;
  /** Optional trend indicator */
  trend?: {
    direction: 'up' | 'down' | 'neutral';
    percentage: number;
    label: string;
  };
  /** Category for grouping */
  category: 'energy' | 'demand' | 'comfort' | 'other';
}

/**
 * End use category
 */
export interface EndUseCategory {
  /** Category name */
  name: string;
  /** Total energy consumption in J */
  value: number;
  /** Energy consumption in kWh */
  valueKWh: number;
  /** Percentage of total */
  percentage: number;
  /** Color for visualization */
  color: string;
}

/**
 * End use colors for charts
 */
const END_USE_COLORS: Record<string, string> = {
  heating: '#ef4444', // red
  cooling: '#3b82f6', // blue
  interior_lights: '#eab308', // yellow
  exterior_lights: '#f59e0b', // amber
  interior_equipment: '#8b5cf6', // purple
  exterior_equipment: '#a855f7', // violet
  fans: '#22c55e', // green
  pumps: '#06b6d4', // cyan
  heat_rejection: '#f97316', // orange
  humidification: '#6366f1', // indigo
  heat_recovery: '#14b8a6', // teal
  water_systems: '#0ea5e9', // light blue
  refrigeration: '#84cc16', // lime
  generators: '#64748b', // slate
  other: '#9ca3af', // gray
};

/**
 * Find meter variables in ESO data
 */
function findMeterVariables(
  data: ESOData,
  pattern: RegExp
): OutputVariable[] {
  const results: OutputVariable[] = [];
  for (const variable of data.variables.values()) {
    if (pattern.test(variable.variableName) || pattern.test(variable.keyValue)) {
      results.push(variable);
    }
  }
  return results;
}

/**
 * Get total energy from a meter variable (convert J to kWh)
 */
function getMeterTotal(data: ESOData, variableId: number): number {
  const timeSeries = data.timeSeries.get(variableId);
  if (!timeSeries) return 0;

  const stats = getTimeSeriesStats(timeSeries.data);
  // Convert J to kWh (1 kWh = 3,600,000 J)
  return stats.sum / 3600000;
}

/**
 * Calculate total site energy consumption
 */
export function calculateTotalSiteEnergy(data: ESOData): number {
  // Look for Facility:Electricity or similar total meters
  const electricityMeters = findMeterVariables(data, /Electricity:Facility/i);
  const gasMeters = findMeterVariables(data, /NaturalGas:Facility|Gas:Facility/i);

  let total = 0;

  for (const meter of electricityMeters) {
    total += getMeterTotal(data, meter.id);
  }

  for (const meter of gasMeters) {
    total += getMeterTotal(data, meter.id);
  }

  // If no facility meters found, try to sum all energy meters
  if (total === 0) {
    const allMeters = findMeterVariables(data, /Meter|Energy/i);
    for (const meter of allMeters) {
      if (meter.units === 'J') {
        total += getMeterTotal(data, meter.id);
      }
    }
  }

  return total;
}

/**
 * Calculate Energy Use Intensity (EUI)
 * Returns kWh/m² (requires building area to be known)
 */
export function calculateEUI(totalEnergy: number, buildingArea: number): number {
  if (buildingArea <= 0) return 0;
  return totalEnergy / buildingArea;
}

/**
 * Calculate peak electric demand
 */
export function calculatePeakDemand(data: ESOData): {
  value: number;
  timestamp: { month: number; day: number; hour: number } | undefined;
} {
  // Look for electricity demand variables
  const demandVars = findMeterVariables(data, /Electricity:Facility|Electric.*Demand/i);

  let peakDemand = 0;
  let peakTimestamp: { month: number; day: number; hour: number } | undefined;

  for (const variable of demandVars) {
    const timeSeries = data.timeSeries.get(variable.id);
    if (!timeSeries) continue;

    const stats = getTimeSeriesStats(timeSeries.data);
    // Convert W to kW
    const maxKW = stats.max / 1000;
    if (maxKW > peakDemand) {
      peakDemand = maxKW;
      if (stats.maxTimestamp) {
        peakTimestamp = {
          month: stats.maxTimestamp.month,
          day: stats.maxTimestamp.day,
          hour: stats.maxTimestamp.hour,
        };
      }
    }
  }

  return { value: peakDemand, timestamp: peakTimestamp };
}

/**
 * Calculate unmet hours from zone temperature data
 */
export function calculateUnmetHours(data: ESOData): {
  heating: number;
  cooling: number;
  total: number;
} {
  // Look for unmet hours variables
  const heatingUnmet = findMeterVariables(
    data,
    /Zone.*Heating.*Unmet|Unmet.*Heating/i
  );
  const coolingUnmet = findMeterVariables(
    data,
    /Zone.*Cooling.*Unmet|Unmet.*Cooling/i
  );

  let heatingHours = 0;
  let coolingHours = 0;

  // Sum up unmet hours
  for (const variable of heatingUnmet) {
    const timeSeries = data.timeSeries.get(variable.id);
    if (timeSeries) {
      const stats = getTimeSeriesStats(timeSeries.data);
      heatingHours += stats.sum;
    }
  }

  for (const variable of coolingUnmet) {
    const timeSeries = data.timeSeries.get(variable.id);
    if (timeSeries) {
      const stats = getTimeSeriesStats(timeSeries.data);
      coolingHours += stats.sum;
    }
  }

  return {
    heating: heatingHours,
    cooling: coolingHours,
    total: heatingHours + coolingHours,
  };
}

/**
 * Calculate end use breakdown
 */
export function calculateEndUseBreakdown(data: ESOData): EndUseCategory[] {
  const endUses: Record<string, number> = {
    heating: 0,
    cooling: 0,
    interior_lights: 0,
    exterior_lights: 0,
    interior_equipment: 0,
    exterior_equipment: 0,
    fans: 0,
    pumps: 0,
    heat_rejection: 0,
    humidification: 0,
    heat_recovery: 0,
    water_systems: 0,
    refrigeration: 0,
    generators: 0,
  };

  // Patterns for identifying end uses
  const patterns: Record<string, RegExp> = {
    heating: /Heating.*Energy|Heat.*Coil|Boiler|Furnace/i,
    cooling: /Cooling.*Energy|Cool.*Coil|Chiller/i,
    interior_lights: /Interior.*Light|Zone.*Light/i,
    exterior_lights: /Exterior.*Light/i,
    interior_equipment: /Interior.*Equipment|Plug|Electric.*Equipment/i,
    exterior_equipment: /Exterior.*Equipment/i,
    fans: /Fan.*Energy|Fan.*Electric/i,
    pumps: /Pump.*Energy|Pump.*Electric/i,
    heat_rejection: /Heat.*Rejection|Cooling.*Tower/i,
    humidification: /Humidif/i,
    heat_recovery: /Heat.*Recovery/i,
    water_systems: /Water.*Heat|DHW|Service.*Hot.*Water/i,
    refrigeration: /Refrigera/i,
    generators: /Generator/i,
  };

  // Find and sum energy for each end use
  for (const [endUse, pattern] of Object.entries(patterns)) {
    const variables = findMeterVariables(data, pattern);
    for (const variable of variables) {
      if (variable.units === 'J' && endUses[endUse] !== undefined) {
        endUses[endUse] += getMeterTotal(data, variable.id);
      }
    }
  }

  // Calculate total
  const total = Object.values(endUses).reduce((a, b) => a + b, 0);

  // Convert to array and calculate percentages
  const results: EndUseCategory[] = [];
  for (const [name, value] of Object.entries(endUses)) {
    if (value > 0) {
      results.push({
        name: formatEndUseName(name),
        value: value * 3600000, // Convert back to J for storage
        valueKWh: value,
        percentage: total > 0 ? (value / total) * 100 : 0,
        color: END_USE_COLORS[name] ?? END_USE_COLORS['other'] ?? '#9ca3af',
      });
    }
  }

  // Sort by value descending
  return results.sort((a, b) => b.value - a.value);
}

/**
 * Format end use name for display
 */
function formatEndUseName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format a large number with appropriate suffix
 */
function formatLargeNumber(value: number, precision: number = 1): string {
  if (Math.abs(value) >= 1000000) {
    return (value / 1000000).toFixed(precision) + 'M';
  }
  if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(precision) + 'k';
  }
  return value.toFixed(precision);
}

/**
 * Generate all summary metrics for the dashboard
 */
export function generateSummaryMetrics(
  data: ESOData,
  buildingArea: number = 0
): SummaryMetric[] {
  const metrics: SummaryMetric[] = [];

  // Total Site Energy
  const totalEnergy = calculateTotalSiteEnergy(data);
  metrics.push({
    title: 'Total Site Energy',
    value: totalEnergy,
    unit: 'kWh',
    displayValue: formatLargeNumber(totalEnergy) + ' kWh',
    category: 'energy',
  });

  // EUI (if building area is known)
  if (buildingArea > 0) {
    const eui = calculateEUI(totalEnergy, buildingArea);
    metrics.push({
      title: 'Energy Use Intensity',
      value: eui,
      unit: 'kWh/m²',
      displayValue: eui.toFixed(1) + ' kWh/m²',
      category: 'energy',
    });
  }

  // Peak Electric Demand
  const peakDemand = calculatePeakDemand(data);
  let peakLabel = formatLargeNumber(peakDemand.value) + ' kW';
  if (peakDemand.timestamp) {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    peakLabel += ` (${monthNames[peakDemand.timestamp.month - 1] ?? ''} ${String(peakDemand.timestamp.day)})`;
  }
  metrics.push({
    title: 'Peak Electric Demand',
    value: peakDemand.value,
    unit: 'kW',
    displayValue: peakLabel,
    category: 'demand',
  });

  // Unmet Hours
  const unmetHours = calculateUnmetHours(data);
  metrics.push({
    title: 'Unmet Hours',
    value: unmetHours.total,
    unit: 'hours',
    displayValue: unmetHours.total.toFixed(0) + ' hours',
    category: 'comfort',
  });

  return metrics;
}

/**
 * Get monthly energy profile data
 */
export function getMonthlyEnergyProfile(
  data: ESOData
): Array<{ month: string; heating: number; cooling: number; other: number }> {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const profile: Array<{ month: string; heating: number; cooling: number; other: number }> = months.map((m) => ({
    month: m,
    heating: 0,
    cooling: 0,
    other: 0,
  }));

  // Find heating and cooling variables
  const heatingVars = findMeterVariables(data, /Heating.*Energy/i);
  const coolingVars = findMeterVariables(data, /Cooling.*Energy/i);

  // Aggregate by month
  const aggregateByMonth = (variables: OutputVariable[], target: 'heating' | 'cooling' | 'other') => {
    for (const variable of variables) {
      const timeSeries = data.timeSeries.get(variable.id);
      if (!timeSeries || variable.units !== 'J') continue;

      for (const point of timeSeries.data) {
        const monthIndex = point.timestamp.month - 1;
        const entry = profile[monthIndex];
        if (monthIndex >= 0 && monthIndex < 12 && entry) {
          entry[target] += point.value / 3600000; // Convert J to kWh
        }
      }
    }
  };

  aggregateByMonth(heatingVars, 'heating');
  aggregateByMonth(coolingVars, 'cooling');

  return profile;
}
